import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiTopProducts,
  apiSummary,
  apiToGetTransactions,
  apiEmployeeGroup,
  apiEmail,
  apiSingleEmployeeData,
  apiToGetBanksList,
  apiToGetEmployerConfig,
  fetchCategories,
  fetchGroupsAndPayDates,
  fetchGroupPaydates,
  fetchMeApi,
  apiInProgress,
  fetchFileNamesData,
  fetchPICUsersAPI,
  fetchInvoiceApi,
  getAccessTypes,
  getLimitRequestsApplication,
  getLimitRequestsDetails,
  getKYBStatus,
  getKYBHistory,
  markNotificationsReadApi,
  markAllNotificationsReadApi,
} from "api/apiCall";
import { useNotifications } from "customHooks/useNotifications";

const READ_KEYS = ["isRead", "is_read", "read", "is_read_notification", "isReadNotification"];

const isUnread = (item) => {
  const readKey = READ_KEYS.find((key) => item?.[key] !== undefined);
  if (readKey) return !item?.[readKey];
  if (typeof item?.status === "string") return item.status.toLowerCase() !== "read";
  return true;
};

const markItemRead = (item) => ({
  ...item,
  isRead: true,
  is_read: true,
  read: true,
  is_read_notification: true,
  isReadNotification: true,
  status: "read",
});

const updateNotificationsCache = ({ queryClient, notificationIds = [], markAll = false }) => {
  const ids = new Set(notificationIds.map((id) => String(id)));

  queryClient.setQueriesData({ queryKey: ["notifications"] }, (current) => {
    if (!current || !Array.isArray(current?.data)) return current;

    const nextData = current.data.map((item) => {
      const shouldMark = markAll || ids.has(String(item?.id));
      return shouldMark ? markItemRead(item) : item;
    });
    const hasUnread = nextData.some(isUnread);

    return {
      ...current,
      data: nextData,
      hasUnreadNotifications: hasUnread,
      has_unread_notifications: hasUnread,
      hasUnreadNotifcations: hasUnread,
    };
  });
};

export function fetchTopProducts() {
  return useQuery(
    {
      queryKey: ["topProducts_api"],
      queryFn: apiTopProducts,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchEmail() {
  return useQuery(
    {
      queryKey: ["email_api"],
      queryFn: apiEmail,
      enabled: true,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchEmployeeGroup() {
  return useQuery(
    {
      queryKey: ["employeeGroup_api"],
      queryFn: apiEmployeeGroup,
      enabled: true,
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchSummary() {
  return useQuery(
    {
      queryKey: ["summary_api"],
      queryFn: apiSummary,
      refetchOnWindowFocus: false,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function getTransactionData(payload) {
  return useQuery(
    {
      queryKey: ["get_transaction_data"],
      queryFn: () => apiToGetTransactions(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getSingleEmployeeData(payload) {
  return useQuery(
    {
      queryKey: ["get_single_employee_data"],
      queryFn: () => apiSingleEmployeeData(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getBanksList() {
  return useQuery(
    {
      queryKey: ["get_banks_list"],
      queryFn: () => apiToGetBanksList(),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getCategoriesList(payload) {
  return useQuery(
    {
      queryKey: ["get_categories_list"],
      queryFn: () => fetchCategories({ requestType: payload }),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getGroupsAndPayDatesList() {
  return useQuery(
    {
      queryKey: ["get_groups_and_pay_dates_list"],
      queryFn: () => fetchGroupsAndPayDates(),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getEmployerConfig() {
  return useQuery(
    {
      queryKey: ["get_employer_config"],
      queryFn: () => apiToGetEmployerConfig(),

      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function fetchGroupPaydatesQuery(payload) {
  return useQuery(
    {
      queryKey: ["fetch_group_dates", payload],
      queryFn: () => fetchGroupPaydates(payload),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function fetchMeApiCall(payload) {
  return useQuery(
    {
      queryKey: ["fetch_me_api", payload],
      queryFn: () => fetchMeApi(payload),
      refetchOnWindowFocus: false,
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchProgressTableData(filterData, currentPage, getPageParams, polling) {
  return useQuery(
    {
      queryKey: ["tableData", filterData, currentPage],
      queryFn: () => apiInProgress(getPageParams()),
      refetchInterval: polling ? 2000 : false,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchfileNamesApi(payload) {
  return useQuery(
    {
      queryKey: ["fetchFileNames", payload],
      queryFn: () => fetchFileNamesData(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchPICUsers() {
  return useQuery(
    { queryKey: ["picUsers"], queryFn: fetchPICUsersAPI, enabled: false },
    { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false },
  );
}
export function fetchInvoices(filterData, currentPage, getParams) {
  return useQuery(
    {
      queryKey: ["fetchInvoices", filterData, currentPage],
      queryFn: () => fetchInvoiceApi(getParams()),
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchAllAccessTypes() {
  return useQuery(
    { queryKey: ["fetchAccessTypes"], queryFn: () => getAccessTypes() },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchLimitRequests(payload) {
  const PAGE_LIMIT = 10;
  return useInfiniteQuery(
    ["limit_requests", payload],
    ({ pageParam = 0 }) => getLimitRequestsApplication({ ...payload, skip: pageParam, limit: PAGE_LIMIT }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage && lastPage.length === PAGE_LIMIT) {
          return PAGE_LIMIT * allPages.length;
        }
        return false;
      },

      staleTime: 5 * 60 * 1000,
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

export function fetchLimitRequestDetails(payload) {
  return useQuery(
    { queryKey: ["fetchLimitRequestDetails"], queryFn: () => getLimitRequestsDetails(payload), enabled: false },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchKYBStatus() {
  return useQuery(
    { queryKey: ["fetchKYBStatus"], queryFn: () => getKYBStatus() },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchKYBHistory() {
  return useQuery(
    { queryKey: ["kybHistory"], queryFn: () => getKYBHistory(), enabled: false },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => markNotificationsReadApi(payload),
    onSuccess: (_, variables) => {
      const ids = Array.isArray(variables?.notificationIds) ? variables.notificationIds : [];
      if (ids.length === 0) return;

      updateNotificationsCache({ queryClient, notificationIds: ids });
      queryClient.invalidateQueries({ queryKey: ["notifications"], refetchType: "active" });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsReadApi(),
    onSuccess: () => {
      updateNotificationsCache({ queryClient, markAll: true });
      queryClient.invalidateQueries({ queryKey: ["notifications"], refetchType: "active" });
    },
  });
}

export function fetchNotifications(payload) {
  return useNotifications(payload);
}
